/**
 * One definition of "where is this document in ingestion, and should we keep asking?".
 *
 * Uploading a document and being able to search it are minutes apart: the upload finishes, then a
 * background job extracts, chunks, moderates and embeds it. Every surface that lists documents has
 * to answer the same two questions while that runs, and before this module three of them answered
 * it separately — the District Knowledge table, the Resource Library, and the custom-chatbot
 * drawer. The copies had already drifted (only one bounded its polling), so the drift, not the
 * duplication, is the reason this is shared.
 *
 * These take the few fields they actually read rather than a whole document row, so every caller's
 * own row type satisfies them structurally — the package's `DocumentIngestionState`, the
 * District Knowledge list item, and the drawer's file entry alike — without anyone converting
 * between shapes or this module depending on any one of them.
 */

/** The ingestion-run fields these helpers read. Any document row carrying them satisfies it. */
export interface DocumentIngestionState {
  /** Latest run's status: pending | running | completed | failed | cancelled, or null if never run. */
  processingStatus: string | null;
  latestIngestionRunId: string | null;
  chunkCount: number | null;
  /** When the latest run was created; falls back to the document's own createdAt for ageing. */
  latestRunCreatedAt: string | null;
  createdAt: string | null;
}

/** How often to re-ask while a document is still being ingested. */
export const INGEST_POLL_INTERVAL_MS = 3000;

/**
 * How long a document may sit unfinished before we stop asking about it.
 *
 * A run that never advances — an offline worker, an orphaned run — would otherwise be polled
 * forever by every open tab. Ten minutes is comfortably longer than a slow large document (a
 * 700-page PDF has been observed taking over six minutes) while still bounding the runaway case.
 */
export const MAX_INGEST_POLL_MS = 10 * 60 * 1000;

/** Still being ingested: the run exists and has not reached a terminal state. */
export const isDocumentIngesting = (doc: DocumentIngestionState): boolean =>
  doc.processingStatus === 'pending' || doc.processingStatus === 'running';

/**
 * Ingestion reached a terminal state without succeeding. Distinct from {@link isDocumentIngesting}
 * because the two warrant opposite things being said: one resolves on its own, the other never
 * will. `cancelled` belongs here rather than with in-flight work — the run is over, and the admin
 * cancel endpoint leaves the document in place, so waiting on it would wait forever.
 */
export const isDocumentIngestFailed = (doc: DocumentIngestionState): boolean =>
  doc.processingStatus === 'failed' || doc.processingStatus === 'cancelled';

/**
 * The document row exists but ingestion never started — no run was ever created. Distinct from a
 * failed run: this is the upload succeeding and the job never being picked up, so there is no error
 * to report and nothing in flight to wait for.
 */
export const isDocumentUnprocessed = (doc: DocumentIngestionState): boolean =>
  doc.processingStatus === null && doc.latestIngestionRunId === null && doc.chunkCount === null;

/** A document is searchable once its ingestion completed; nothing else in this list can be retrieved. */
export const isDocumentSearchable = (doc: DocumentIngestionState): boolean => doc.processingStatus === 'completed';

/**
 * Whether this list still warrants polling: some document is unfinished AND its run is recent
 * enough to plausibly still be working. Age is taken from the run's own creation time, falling back
 * to the document's, so a row with neither cannot poll forever on a missing timestamp.
 */
export const shouldPollDocuments = (docs: DocumentIngestionState[], now: number = Date.now()): boolean =>
  docs.some((doc) => {
    if (!isDocumentIngesting(doc)) return false;

    const basis = doc.latestRunCreatedAt ?? doc.createdAt;
    const startedAt = basis ? new Date(basis).getTime() : Number.NaN;
    // No usable timestamp: keep polling rather than stall a document that may well be live.
    if (Number.isNaN(startedAt)) return true;

    return now - startedAt < MAX_INGEST_POLL_MS;
  });

/** `refetchInterval` for a react-query document list: poll while work is in flight, then stop. */
export const documentPollInterval = (docs: DocumentIngestionState[] | undefined): number | false =>
  shouldPollDocuments(docs ?? []) ? INGEST_POLL_INTERVAL_MS : false;

/**
 * A document's ingestion state as the file-list UI names it.
 *
 * Lives here rather than in the drawer so the mapping from run status to what a person sees is
 * defined once, next to the predicates it is built from. `idle` is deliberately the *searchable*
 * state, not merely the uploaded one — every caller already treats `idle` as "ready to use", and a
 * document that is stored but still indexing is not.
 */
export const toFileState = (doc: DocumentIngestionState): 'idle' | 'processing' | 'failed' => {
  if (isDocumentIngestFailed(doc)) return 'failed';
  if (isDocumentSearchable(doc)) return 'idle';
  // Everything else — in flight, or a row whose job never started — is still pending from the
  // uploader's point of view: both resolve by waiting, unlike the terminal states above.
  return 'processing';
};
